/**
 * The state machine for one in-flight agent turn.
 *
 * Pure: no React, no Apollo, no socket. That matters because the guards below are
 * the only thing standing between us and a handful of races that are very hard to
 * reproduce by hand — a stale run's tokens bleeding into a newer answer, a
 * duplicate completion unlocking the composer mid-turn, the websocket beating the
 * HTTP response. They are unit tested instead.
 *
 * It returns the next state AND what to do with the streamed-text buffer, because
 * the accept/drop decision is the same for both and must not be made twice.
 */
import type { AgentStreamEvent, AgentToolCall } from "./types";

export type TurnPhase =
  | "idle"
  /** The server has the message; the agent has not started answering. */
  | "queued"
  | "running"
  /** Answered, but the message rows have not landed in the cache yet. */
  | "settling"
  | "failed";

export type TurnState = {
  runId: string | null;
  phase: TurnPhase;
  tools: AgentToolCall[];
  error: string | null;
  /** Feeds the stall watchdog. */
  lastEventAt: number;
  /**
   * When this turn was adopted. Feeds the "llevamos 20s…" cue.
   *
   * Distinct from `lastEventAt`, and it has to be: a turn that is streaming steadily
   * refreshes `lastEventAt` on every token, so it would report an elapsed time of
   * ~0ms forever. What the user is counting is how long they have been waiting for
   * *this turn*, not how long since it last twitched.
   */
  startedAt: number;
  /**
   * We reconnected mid-run and dropped the text streamed so far. The resume replays
   * what we missed, not what we already had, so whatever streams in now starts
   * mid-sentence. Render a spinner rather than a partial answer that silently lies
   * about where it starts; `RUN_COMPLETED` carries the full text and ends it.
   */
  desynced: boolean;
};

/** What the caller should do to the streamed-text buffer as a result of an action. */
export type BufferOp =
  | { kind: "none" }
  | { kind: "clear" }
  | { kind: "append"; text: string }
  | { kind: "set"; text: string };

export type TurnAction =
  | { kind: "EVENT"; event: AgentStreamEvent; at: number }
  /** `actOnSession` came back with the run it queued. */
  | { kind: "SEND_ACCEPTED"; runId: string; at: number }
  /** `actOnSession` failed, or the backend refused it. Nothing was persisted. */
  | { kind: "SEND_REJECTED" }
  | { kind: "RECONNECTED"; at: number }
  /** The answer is durable in the cache now; the overlay can retire. */
  | { kind: "SETTLED" }
  | { kind: "DISMISS_ERROR" };

export type TurnResult = { state: TurnState; buffer: BufferOp };

export const initialTurnState: TurnState = {
  runId: null,
  phase: "idle",
  tools: [],
  error: null,
  lastEventAt: 0,
  startedAt: 0,
  desynced: false,
};

const GENERIC_FAILURE = "El asistente no pudo responder. Vuelve a intentarlo.";

/** A turn we are still expecting events from. */
export const isTurnActive = (phase: TurnPhase): boolean =>
  phase === "queued" || phase === "running";

const unchanged = (state: TurnState): TurnResult => ({
  state,
  buffer: { kind: "none" },
});

/** Take over a run, from scratch. Used both to start our own and to adopt someone else's. */
const adopt = (runId: string, phase: TurnPhase, at: number): TurnResult => ({
  state: {
    runId,
    phase,
    tools: [],
    error: null,
    lastEventAt: at,
    startedAt: at,
    desynced: false,
  },
  buffer: { kind: "clear" },
});

const applyEvent = (
  state: TurnState,
  event: AgentStreamEvent,
  at: number,
): TurnResult => {
  // RUN_STARTED is the one event the server can send about a run we know nothing
  // about — a turn started in another tab, or one drained after a reconnect. So it
  // always wins, and adopting it is what makes a second tab mirror the stream.
  //
  // Unless it is the run we already track, in which case it must be idempotent:
  // resetting here would wipe deltas that already arrived, because the event
  // routinely beats the action mutation's HTTP response back (Redis -> websocket is
  // faster than an HTTP round trip). That looks like "the first few tokens go
  // missing" and it is maddening to debug.
  if (event.type === "RUN_STARTED") {
    // Background work (a form's options job) feeds the form state only. Adopting its
    // RUN_STARTED would lock the composer and show a thinking bubble for a job the
    // person is not waiting on. Only RUN_STARTED needs the check: a run the turn never
    // adopted has its RUN_COMPLETED and RUN_FAILED dropped by the run-id guard below.
    if (event.background) return unchanged(state);
    if (state.runId === event.runId) {
      // Same run: only an active phase actually needs the reset (a duplicate
      // while still queued/running is folded into "running"). Anything else —
      // settling, failed, idle — has already moved past this run, and adopting
      // again would regress a settled turn back to "running", wipe its text,
      // and strand the retirement effect that is waiting on the old phase.
      if (isTurnActive(state.phase)) {
        return {
          state: { ...state, phase: "running", lastEventAt: at },
          buffer: { kind: "none" },
        };
      }
      return unchanged(state);
    }
    return adopt(event.runId, "running", at);
  }

  // Everything else must belong to the run we are tracking, and that run must
  // still be live. These are exactly the events a zombie emits: the backend can
  // sweep a run it believes dead while its task is still running, and that task
  // will happily go on publishing. Without this guard its tokens end up
  // concatenated into a *newer* answer, or its completion unlocks the composer in
  // the middle of a different turn.
  //
  // The phase half is not redundant: a duplicate RUN_COMPLETED for the current run
  // passes the id check on its own.
  if (event.runId !== state.runId || !isTurnActive(state.phase)) {
    return unchanged(state);
  }

  switch (event.type) {
    case "HEARTBEAT":
      // Pure liveness: the model is generating something that never streams as
      // text (tool arguments, thinking). Only the stall clock moves.
      return {
        state: { ...state, lastEventAt: at },
        buffer: { kind: "none" },
      };

    case "TEXT_DELTA":
      return {
        state: { ...state, phase: "running", lastEventAt: at },
        buffer: { kind: "append", text: event.delta ?? "" },
      };

    case "TOOL_CALL_STARTED":
      return {
        state: {
          ...state,
          phase: "running",
          lastEventAt: at,
          tools: [
            ...state.tools,
            {
              toolCallId: event.toolCallId ?? "",
              toolName: event.toolName ?? "",
              done: false,
            },
          ],
        },
        buffer: { kind: "none" },
      };

    case "TOOL_CALL_FINISHED":
      return {
        state: {
          ...state,
          lastEventAt: at,
          tools: state.tools.map((tool) =>
            tool.toolCallId === event.toolCallId
              ? { ...tool, done: true }
              : tool,
          ),
        },
        buffer: { kind: "none" },
      };

    case "RUN_COMPLETED":
      // The event carries the FULL answer, not just the tail — so this also
      // repairs a stream that lost deltas to a reconnect.
      return {
        state: {
          ...state,
          phase: "settling",
          tools: [],
          error: null,
          desynced: false,
          lastEventAt: at,
        },
        buffer: { kind: "set", text: event.text ?? "" },
      };

    case "RUN_FAILED":
      // Drop the partial answer. A truncated half-sentence sitting above an error
      // reads as though it were the reply; nothing was persisted, so it is not.
      return {
        state: {
          ...state,
          phase: "failed",
          tools: [],
          error: event.error || GENERIC_FAILURE,
          lastEventAt: at,
        },
        buffer: { kind: "clear" },
      };

    // The form-first jobs report their progress as their own events: a concept landing,
    // a script saved. The script's text streams as TEXT_DELTA, but these markers carry
    // none — for the turn they are pure liveness. Without this the stall watchdog
    // would call a healthy 40s concepts job dead.
    case "CONCEPT_READY":
    case "CONCEPTS_COMPLETED":
    case "SCRIPT_READY":
      return {
        state: { ...state, lastEventAt: at },
        buffer: { kind: "none" },
      };

    // A select's options are background work the person is not waiting on, and they
    // are read by `reduceFormLive`. Never a turn's business.
    case "FORM_OPTIONS":
      return unchanged(state);

    // Research events belong to a background job, not this turn — `reduceResearch`
    // (via `AgentTurnStore.dispatch`) is what reacts to them. The turn just ignores
    // them, same as any event for a run it is not tracking.
    case "RESEARCH_STARTED":
    case "RESEARCH_PROGRESS":
    case "RESEARCH_COMPLETED":
    case "RESEARCH_FAILED":
      return unchanged(state);
  }
};

/**
 * A background content-research job's progress card.
 *
 * Deliberately NOT part of `TurnState`: a research job is correlated by `jobId`,
 * not `runId`, and it keeps going (and must keep being shown) after the run that
 * kicked it off has already settled — `TurnState` resets to `initialTurnState`
 * on every SETTLED/SEND_REJECTED/adopt, which would wipe it mid-job.
 */
export type ResearchState = {
  jobId: string | null;
  stage: string | null;
  /** Rolling trail of progress lines, newest last, capped so the card stays short. */
  messages: string[];
  done: boolean;
  usedFallback: boolean;
  failed: boolean;
  /** RESEARCH_COMPLETED: how many outlier contents were found. */
  outlierCount: number;
  /** Top winner thumbnail URLs (up to 6), in order. Streams in early via
   * RESEARCH_PROGRESS (evidence from previous scrapes) and is replaced by the
   * definitive set on RESEARCH_COMPLETED. */
  thumbnails: string[];
  /**
   * The job finished, AND the turn that was streaming when it finished has since
   * settled. Once this flips, `AnimationSlot`'s fallback path must stop showing
   * the deck — otherwise it resurfaces during every later streaming gap (a
   * pricing turn, a script being written) that has no tool family of its own,
   * which reads as the research flow running backwards.
   *
   * Deliberately NOT the same thing as `done`: a job can finish while its turn is
   * still writing the concept prose that follows it, and the deck should stay up
   * for that stretch. It is `RESEARCH_STARTED` — a fresh job — that clears this,
   * not `done` going true.
   */
  retired: boolean;
};

export const initialResearchState: ResearchState = {
  jobId: null,
  stage: null,
  messages: [],
  done: false,
  usedFallback: false,
  failed: false,
  outlierCount: 0,
  thumbnails: [],
  retired: false,
};

const RESEARCH_MESSAGE_CAP = 6;

const pushResearchMessage = (messages: string[], message: string): string[] =>
  [...messages, message].slice(-RESEARCH_MESSAGE_CAP);

/** Companion to `reduceTurn`: same wire events, a state machine of its own. */
export const reduceResearch = (
  state: ResearchState,
  event: AgentStreamEvent,
): ResearchState => {
  switch (event.type) {
    case "RESEARCH_STARTED":
      // A fresh job always wins, even mid-job — the same job never restarts, so a
      // second RESEARCH_STARTED means a brand new investigation superseded this one.
      return { ...initialResearchState, jobId: event.jobId, stage: "QUEUED" };

    case "RESEARCH_PROGRESS":
      if (event.jobId !== state.jobId) return state;
      return {
        ...state,
        stage: event.stage ?? state.stage,
        messages: pushResearchMessage(state.messages, event.detail ?? ""),
        // Progress events carry thumbnails from PREVIOUS scrapes so the deck
        // fills while this run is still working; an event without any keeps
        // whatever is already showing rather than blanking the deck.
        thumbnails: event.thumbnails?.length
          ? event.thumbnails
          : state.thumbnails,
      };

    case "RESEARCH_COMPLETED":
      if (event.jobId !== state.jobId) return state;
      return {
        ...state,
        done: true,
        usedFallback: !!event.usedFallback,
        outlierCount: event.outlierCount ?? 0,
        thumbnails: event.thumbnails ?? [],
        messages: pushResearchMessage(
          state.messages,
          event.usedFallback
            ? "Usando referentes de la industria"
            : `Análisis listo: ${event.outlierCount ?? 0} contenidos outlier detectados`,
        ),
      };

    case "RESEARCH_FAILED":
      if (event.jobId !== state.jobId) return state;
      return {
        ...state,
        done: true,
        failed: true,
        messages: pushResearchMessage(
          state.messages,
          "No pudimos completar la investigación",
        ),
      };

    default:
      return state;
  }
};

/**
 * Called by the store when a turn that was streaming finishes settling — see
 * `TurnAction`'s `SETTLED`. If the research job that turn was narrating has
 * already finished, retire its fallback card for good; a job still in flight is
 * left alone, because the deck must keep showing until it actually completes.
 *
 * A no-op (returns the same reference) when there is nothing to retire, so
 * `AgentTurnStore` can skip the notification the same way `reduceResearch` does.
 */
export const retireResearchOnSettle = (state: ResearchState): ResearchState => {
  if (!state.done || state.retired) return state;
  return { ...state, retired: true };
};

export const reduceTurn = (
  state: TurnState,
  action: TurnAction,
): TurnResult => {
  switch (action.kind) {
    case "EVENT":
      return applyEvent(state, action.event, action.at);

    case "SEND_ACCEPTED":
      // The websocket may have adopted this run already — see RUN_STARTED above.
      if (state.runId === action.runId) return unchanged(state);
      return adopt(action.runId, "queued", action.at);

    case "SEND_REJECTED":
      // Nothing was persisted, so there is no turn. The caller restores the draft.
      return { state: initialTurnState, buffer: { kind: "clear" } };

    case "RECONNECTED":
      if (!isTurnActive(state.phase)) return unchanged(state);
      return {
        state: { ...state, desynced: true, lastEventAt: action.at },
        buffer: { kind: "clear" },
      };

    case "SETTLED":
      // Guarded: only the turn we were waiting on can retire. Otherwise a late
      // dispatch could wipe a turn that has already started.
      if (state.phase !== "settling") return unchanged(state);
      return { state: initialTurnState, buffer: { kind: "clear" } };

    case "DISMISS_ERROR":
      if (state.phase !== "failed") return unchanged(state);
      return { state: initialTurnState, buffer: { kind: "clear" } };
  }
};
